import type { investor_profile, referral, user } from "@prisma/client";
import { InputPassword } from "@r8er/components/input/password";
import { InputSelect } from "@r8er/components/input/select";
import { InputShortText } from "@r8er/components/input/short-text";
import { ButtonContainer } from "@r8er/components/marketing/button";
import buttonClasses from "@r8er/components/marketing/button/button.module.css";
import { Column, Columns } from "@r8er/components/marketing/columns";
import columnClasses from "@r8er/components/marketing/columns/columns.module.css";
import { Container } from "@r8er/components/marketing/container";
import { Divider } from "@r8er/components/marketing/divider";
import { IconLogoMarkLight } from "@r8er/components/marketing/icons";
import { Layout } from "@r8er/components/marketing/layout";
import layoutClasses from "@r8er/components/marketing/layout/layout.module.css";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { ReCAPTCHA } from "@r8er/components/recaptcha";
import { api } from "@r8er/lib/api";
import { useStateCallback } from "@r8er/lib/client";
import { EMAIL_ADDRESSES } from "@r8er/lib/config/email-addresses";
import {
  isSectorValue,
  parseSector,
  SECTOR_OPTIONS,
  SECTOR_PROMPT,
} from "@r8er/lib/config/sectors";
import { sendEmail } from "@r8er/lib/mail";
import { normaliseWebsiteEnquiryParameters } from "@r8er/lib/mail/enquiry-parameters";
import { EmailTemplate } from "@r8er/lib/mail/templates";
import { recordUndeliveredEnquiry } from "@r8er/lib/mail/undelivered-enquiry";
import { withMiddleware } from "@r8er/lib/middleware";
import { referral as referralProc } from "@r8er/lib/procedures/referral";
import {
  register,
  REGISTRATION_ERRORS,
  validateAllRecaptcha,
} from "@r8er/lib/procedures/register";
import type { InferGetServerSidePropsType } from "next";
import { log } from "@r8er/lib/logging";
import Head from "next/head";
import type { FormEventHandler } from "react";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

type Referrer = referral & {
  referrer: user & {
    investor_profile: investor_profile;
  };
};

/** Worded to match the checkbox label rendered further down this page. */
const TERMS_REQUIRED_ERROR =
  "You must accept the R8ER Website Terms of Service.";
const ENQUIRY_DELIVERY_ERROR =
  "We couldn't submit your enquiry at this time. Please try again.";

/**
 * How long the sign-up response will wait on the admin notification. The
 * account is already committed by the time it is sent, so a slow third party
 * must not keep the visitor on the spinner or push the function towards its
 * execution limit.
 */
const NOTIFICATION_TIMEOUT_MS = 3000;

/**
 * Names the referrer for the admin notification. referral.referred_by points at
 * an optional relation, so every part of it has to be treated as missable.
 */
const describeReferrer = (record: Referrer) => {
  const referrer = record.referrer;
  const name = [referrer?.firstname, referrer?.lastname]
    .filter(Boolean)
    .join(" ");

  if (name && referrer?.email) return `${name} (${referrer.email})`;
  return name || referrer?.email || "an existing R8ER member";
};

export const getServerSideProps = withMiddleware(
  async ({ req, query: { referral: referralToken }, prisma }) => {
    let referralRecord: Referrer | undefined;

    try {
      if (typeof referralToken === "string") {
        const referrerData = referralProc.parseReferralToken(referralToken);

        referralRecord = await prisma.referral.findFirst({
          where: {
            id: referrerData?.referralId,
            accepted_timestamp: null,
          },
          include: {
            referrer: {
              include: {
                investor_profile: true,
              },
            },
          },
        });
      }
    } catch {}

    const showRegistration =
      !!referralRecord || process.env.HIDE_REGISTRATION !== "true";
    let needV2Captcha = false;

    const body = await register.parse(req);
    const {
      accountType,
      email,
      firstname,
      lastname,
      companyName,
      sector,
      optinTerms,
      optinNewsletter,
      "recaptcha-token": recaptchaToken,
      "recaptcha-token-v2": recaptchaV2Token,
    } = body;

    const errors = [];
    let success = false;

    // register()'s switch implements only these two. Anything else must fall
    // through to the enquiry branch: with no matching case it creates no user,
    // leaves user_id null, and then emails a sign-in token generated from it.
    // referral.type is a free-text column, so a referral record alone is not
    // enough to trust the incoming accountType.
    const REGISTERABLE_ACCOUNT_TYPES = ["entrepreneur", "investor"] as const;
    // A referral's stored type is authoritative. Otherwise a crafted Partner
    // referral POST can change accountType to Investor and create an account.
    const effectiveAccountType = referralRecord?.type || accountType;
    const registerableAccountType = REGISTERABLE_ACCOUNT_TYPES.find(
      (type) => type === effectiveAccountType,
    );
    // Climate is a waitlist rather than an available R8ER sector. Keep the
    // submitted details as an admin enquiry, but never create an account or
    // consume a referral before the sector is live.
    const climateWaitlist = parseSector(sector) === "climate";

    if ("POST" === req.method) {
      if (
        !climateWaitlist &&
        (referralRecord || effectiveAccountType === "entrepreneur") &&
        registerableAccountType
      ) {
        await register(
          prisma,
          {
            referrerId: referralRecord?.referrer?.id,
            sponsorId:
              referralRecord?.sponsored &&
              referralRecord?.referrer?.investor_profile?.id,
            referralRecord: referralRecord,
          },
          { ...body, accountType: registerableAccountType },
        ).catch((e) => {
          if (e === REGISTRATION_ERRORS.TERMS_REQUIRED)
            errors.push(TERMS_REQUIRED_ERROR);
          else if (e === REGISTRATION_ERRORS.CONFIRMATION_MISMATCH)
            errors.push("Confirmation password does not match.");
          else if (e === REGISTRATION_ERRORS.UNKNOWN_ERROR)
            errors.push(
              "We couldn't sign you up at this time. Please contact us for further help",
            );
          else if (e === REGISTRATION_ERRORS.COMPANY_NAME_REQUIRED)
            errors.push("Company name is required");
          else if (e === REGISTRATION_ERRORS.FIRST_NAME_REQUIRED)
            errors.push("First name is required");
          else if (e === REGISTRATION_ERRORS.LAST_NAME_REQUIRED)
            errors.push("Last name is required");
          else if (e === REGISTRATION_ERRORS.PASSWORD_LENGTH)
            errors.push(
              "Password did not meet the minimum length requirements.",
            );
          else if (e === REGISTRATION_ERRORS.CAPTCHA_ERROR)
            errors.push(
              "We were not able to sign you up at this time. Please try again or contact us for further help.",
            );
          else if (e === REGISTRATION_ERRORS.V2_CAPTCHA_NEEDED)
            needV2Captcha = true;
          else if (e.duplicate)
            errors.push(
              "Some of the data you entered already exists in the system. If you think this is a mistake please contact us.",
            );
          else
            errors.push(
              "We couldn't sign you up at this time. Please try again or contact us for further help.",
            );
        });

        // A v2 challenge pushes no error, but register() throws it before the
        // switch that creates the account, so this is not a completed sign-up:
        // nothing to notify the admin inbox about, and the form has to render
        // again to carry the challenge.
        success = errors.length == 0 && !needV2Captcha;

        const shouldNotifyAdmin =
          registerableAccountType === "entrepreneur" ||
          (registerableAccountType === "investor" && !!referralRecord);

        if (success && shouldNotifyAdmin) {
          // Account registrations bypass the enquiry branch above. Preserve
          // the website-enquiry notification for entrepreneurs and referred
          // investors so a completed referral is visible to the admin inbox.
          const accountSubject =
            registerableAccountType === "entrepreneur" ? "company" : "investor";
          const notificationContext = `${registerableAccountType} sign-up notification`;
          const notificationLogContext = `${registerableAccountType[0].toUpperCase()}${registerableAccountType.slice(1)} sign-up notification`;
          const notificationData = {
            accountType: registerableAccountType,
            firstName: firstname,
            lastName: lastname,
            companyName,
            email,
            sector: parseSector(sector),
            // A referred account did not sign itself up: say who referred it
            // instead of misdescribing the sign-up to the admin inbox.
            message: referralRecord
              ? `This ${accountSubject} was referred to R8ER by ${describeReferrer(
                  referralRecord,
                )} and completed an ${registerableAccountType} sign-up from the referral link.`
              : "This company signed itself up for an entrepreneur account on R8ER via the sign-up form.",
            ...normaliseWebsiteEnquiryParameters({
              optin_terms: optinTerms,
              optin_newsletter: optinNewsletter,
            }),
          };

          const notification = sendEmail(
            EMAIL_ADDRESSES.CONTACT,
            notificationData,
            EmailTemplate.Enquiry,
          ).catch((e) => {
            // The account already exists, so a failed notification must not
            // turn a successful sign-up into an error for the visitor.
            log.error(`${notificationLogContext} failed`, {
              error: `${e}`,
            });

            return null;
          });

          // register() has already committed the account and awaited its own
          // SendGrid sends, so this fourth one must not hold the sign-up
          // response open for however long SendGrid takes. Only the wait is
          // bounded: the send itself is left to finish.
          let notificationTimer: ReturnType<typeof setTimeout> | undefined;
          // sendEmail resolves to null when the provider rejects the send, so a
          // resolved promise is not on its own proof that anything was sent.
          const notificationOutcome = await Promise.race([
            notification.then((sent) => (sent === null ? "failed" : "sent")),
            new Promise<string>((resolve) => {
              notificationTimer = setTimeout(
                () => resolve("timed out"),
                NOTIFICATION_TIMEOUT_MS,
              );
            }),
          ]);
          clearTimeout(notificationTimer);

          if (notificationOutcome === "timed out")
            log.error(`${notificationLogContext} timed out`, {
              timeoutMs: NOTIFICATION_TIMEOUT_MS,
            });

          // This notification is the only admin-facing record of the completed
          // registration, so keep a recoverable copy if delivery fails.
          if (notificationOutcome !== "sent")
            await recordUndeliveredEnquiry(
              prisma,
              notificationData,
              `${notificationContext} ${notificationOutcome}`,
            );
        }
      } else if (!optinTerms) {
        // register() enforces this for accounts, but the enquiry branch never
        // did, so an unticked box was accepted and recorded as "Terms Opt-in:
        // No" in the admin email.
        errors.push(TERMS_REQUIRED_ERROR);
      } else {
        const enquiryData = {
          accountType: effectiveAccountType,
          firstName: firstname,
          lastName: lastname,
          companyName,
          email,
          // Optional: the select posts nothing until the visitor picks an
          // option, and an unanswered question must not fail the enquiry.
          sector: parseSector(sector),
          // The referral type is free text, so a hardcoded article produces
          // copy such as "an partner". Quoting it is correct for every type.
          message: `This user has requested a "${effectiveAccountType}" account on R8ER via the sign-up form.`,
          ...normaliseWebsiteEnquiryParameters({
            optin_terms: optinTerms,
            optin_newsletter: optinNewsletter,
          }),
        };

        // Only recover a submission after CAPTCHA verification. The route is
        // unauthenticated, so persisting rejected submissions would let a bot
        // write arbitrary rows through the public form.
        let captchaPassed = false;

        try {
          await validateAllRecaptcha(recaptchaToken, recaptchaV2Token);
          captchaPassed = true;
          const sent = await sendEmail(
            EMAIL_ADDRESSES.CONTACT,
            enquiryData,
            EmailTemplate.Enquiry,
          );

          // The email is the only artefact this branch produces, and sendEmail
          // reports a rejected send by resolving to null rather than throwing,
          // so a rejected delivery must not render a false thank-you notice.
          if (sent === null) {
            await recordUndeliveredEnquiry(
              prisma,
              enquiryData,
              "sign-up enquiry send failed",
            );
            errors.push(ENQUIRY_DELIVERY_ERROR);
          }
        } catch (error) {
          if (error === REGISTRATION_ERRORS.CAPTCHA_ERROR)
            errors.push(
              "We were not able to sign you up at this time. Please try again or contact us for further help.",
            );
          else if (error === REGISTRATION_ERRORS.V2_CAPTCHA_NEEDED) {
            // Asking for the CAPTCHA without raising this leaves the form on
            // v3, so the challenge never appears and the visitor can never
            // satisfy the message. The register() arm above already sets it.
            needV2Captcha = true;
            errors.push(
              "We couldn't verify that you are a real human. Please complete the CAPTCHA and try again.",
            );
          } else {
            if (captchaPassed)
              await recordUndeliveredEnquiry(
                prisma,
                enquiryData,
                "sign-up enquiry delivery threw",
              );
            errors.push(ENQUIRY_DELIVERY_ERROR);
          }
        }
      }

      success = errors.length == 0 && !needV2Captcha;
    }
    return {
      props: {
        success,
        showRegistration,
        needV2Captcha,
        referralRecord,
        errors,
        type: effectiveAccountType,
        email,

        firstname,
        lastname,
        companyName,
        sector,
      },
    };
  },
  { mode: "unauthenticated" },
);

const Watermark = () => (
  <svg
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: "translate(-75%, -15%)",
      opacity: "50%",
    }}
    xmlns="http://www.w3.org/2000/svg"
    width="649.6"
    height="1051.7"
  >
    <path
      fill="#f0f0f0"
      d="M516 79a270 270 0 00-382 0 270 270 0 00-23 356l49-48c-56-79-49-190 22-260a203 203 0 01286 0c79 79 79 207 0 286L325 556l-74-73-48 47 122 122 191-191a270 270 0 000-382"
    />
    <path
      fill="#f0f0f0"
      d="M555 497l-15-14-48 47 15 15a258 258 0 010 364 258 258 0 01-364 0 258 258 0 010-364l182-182 74 74 48-48-122-122L95 497a325 325 0 000 460 325 325 0 00460 0 325 325 0 000-460"
    />
  </svg>
);

const Register = ({
  success,
  type,
  email,

  firstname,
  lastname,
  companyName,
  sector,
  referralRecord,
  errors: errorsStr,
  needV2Captcha,
}: Omit<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  "showRegistration"
>) => {
  const [recaptchaToken, setRecaptchaToken] = useStateCallback<string>("");
  const [errors, setErrors] = useState<string[]>(errorsStr);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const SubmitForm: FormEventHandler<HTMLFormElement> = async (e) => {
    log.info(`SubmitForm`);
    e.preventDefault();
    setErrors(errorsStr);
    const passwordStrengthElement =
      e?.currentTarget?.elements?.["password-strength"];
    const passwordFeedbackWarningElement =
      e?.currentTarget?.elements?.["password-feedback-warning"];
    const passwordFeedbackSuggestionsElement =
      e?.currentTarget?.elements?.["password-feedback-suggestions"];
    if (passwordStrengthElement) {
      const passwordStrength = parseInt(passwordStrengthElement.value);
      if (passwordStrength < 2) {
        return setErrors((e) => [
          ...e,
          `Password too weak: ${
            passwordFeedbackWarningElement?.value ||
            passwordFeedbackSuggestionsElement?.value ||
            "Password too short. Please enter at least 4 characters"
          }`,
        ]);
      }
    }
    // A v2 retry posts the challenge response directly. That form deliberately
    // contains no v3 input, so do not request another v3 token first.
    if (needV2Captcha) {
      e.currentTarget.submit();
      return;
    }

    if (!executeRecaptcha) {
      setErrors((current) => [
        ...current,
        "We couldn't verify that you are a real human. Please try again.",
      ]);
      return;
    }

    const gReCaptchaToken = await executeRecaptcha("enquiryFormSubmit");

    setRecaptchaToken(gReCaptchaToken, (token) => {
      // @ts-ignore
      e.target.submit();
    });
  };

  const [accountType, setAccountType] = useState(type ?? "entrepreneur");

  const accountTypeOptions = [
    { value: "entrepreneur", label: "I am an entrepreneur..." },
    { value: "investor", label: "I am an investor..." },
    // Partner and Expert reuse the existing investor-style enquiry path. The
    // branch in getServerSideProps creates an account only for entrepreneurs or
    // matching Entrepreneur/Investor referrals, so both always fall through to
    // the Enquiry email and never render account-password inputs.
    { value: "partner", label: "I am a partner..." },
    { value: "expert", label: "I am an expert..." },
  ];

  const headings: Record<string, string> = {
    investor: "Request an Invitation",
    partner: "Register your interest as a R8ER partner",
    expert: "Register your interest as a R8ER expert",
    assessor: "Register your interest as a R8ER expert",
  };

  // A referral's stored type is authoritative on the server. Keep the select
  // locked to that value even when a legacy/free-text type is not part of the
  // public list; showing all choices would imply a choice the server ignores.
  const matchedReferralOption = referralRecord?.type
    ? accountTypeOptions.find((option) => option.value === referralRecord.type)
    : undefined;
  const visibleAccountTypeOptions = referralRecord?.type
    ? [
        matchedReferralOption ?? {
          value: referralRecord.type,
          label:
            referralRecord.type === "assessor"
              ? "I am an expert..."
              : `Referral account: ${referralRecord.type}`,
        },
      ]
    : accountTypeOptions;

  return (
    <div>
      <Head>
        <title>Register Interest - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <div style={{ position: "relative", overflow: "hidden" }}>
          <Watermark />

          <Container width={1100}>
            <h1
              style={{
                marginTop: "5rem",
                marginBottom: "-4rem",
                color: "var(--brand-secondary)",
                textAlign: "center",
                fontSize: "2.625rem",
                fontWeight: "normal",
              }}
            >
              {headings[accountType] ?? "Sign up to R8ER"}
            </h1>
            <Divider />
          </Container>

          <Container width={1100}>
            <div>
              {success && (
                <div
                  style={{ marginBottom: "7.5rem" }}
                  className={layoutClasses.marketing__notice}
                >
                  {sector === "climate"
                    ? "Thanks for your interest in R8ER! We have noted your contact information and will be in touch once we are live in the climate sector."
                    : "Thank you for registering. A member of the R8ER team will be in touch as soon as possible."}
                </div>
              )}

              {!success && (
                <form method="post" onSubmitCapture={SubmitForm}>
                  {referralRecord?.sponsored && (
                    <div>Sponsored registration</div>
                  )}
                  <Columns count={2}>
                    <Column style={{ overflow: "visible" }}>
                      <InputSelect
                        id="accountType"
                        name="accountType"
                        placeholder="Account type"
                        defaultValue={type ? type : "entrepreneur"}
                        onChange={(e) =>
                          setAccountType(e.newValue as unknown as string)
                        }
                        options={visibleAccountTypeOptions}
                        theme={{
                          selectedOptionContainer:
                            layoutClasses.marketing__layout_input_selectedOptionsContainer,
                          selectContainer:
                            layoutClasses.marketing__layout_input_selectContainer,
                          selectButton:
                            layoutClasses.marketing__layout_input_selectButton,
                          activeButton:
                            layoutClasses.marketing__layout_input_activeButton,
                          selectOptions:
                            layoutClasses.marketing__layout_input_selectOptions,
                          selectOption:
                            layoutClasses.marketing__layout_input_selectOption,
                        }}
                      />
                      <InputShortText
                        id="firstname"
                        name="firstname"
                        placeholder="First name"
                        inputMode="text"
                        defaultValue={firstname}
                        required
                      />
                      <InputShortText
                        id="lastname"
                        name="lastname"
                        placeholder="Last name"
                        inputMode="text"
                        defaultValue={lastname}
                        required
                      />
                      <InputShortText
                        id="companyName"
                        name="companyName"
                        placeholder="Company name"
                        inputMode="text"
                        defaultValue={companyName}
                        required
                      />
                    </Column>
                    <Column style={{ overflow: "visible" }}>
                      <InputShortText
                        id="email"
                        name="email"
                        defaultValue={referralRecord?.email ?? email ?? ""}
                        placeholder="Email"
                        inputMode="email"
                        required
                      />

                      <InputSelect
                        id="sector"
                        name="sector"
                        placeholder={SECTOR_PROMPT}
                        defaultValue={
                          isSectorValue(sector) ? sector : undefined
                        }
                        options={SECTOR_OPTIONS.map(({ value, label }) => ({
                          value,
                          label,
                        }))}
                        theme={{
                          selectedOptionContainer:
                            layoutClasses.marketing__layout_input_selectedOptionsContainer,
                          selectContainer:
                            layoutClasses.marketing__layout_input_selectContainer,
                          selectButton:
                            layoutClasses.marketing__layout_input_selectButton,
                          activeButton:
                            layoutClasses.marketing__layout_input_activeButton,
                          selectOptions:
                            layoutClasses.marketing__layout_input_selectOptions,
                          selectOption:
                            layoutClasses.marketing__layout_input_selectOption,
                        }}
                      />

                      {(accountType === "entrepreneur" ||
                        (accountType === "investor" && !!referralRecord)) && (
                        <div>
                          <InputPassword
                            id="password"
                            name="password"
                            placeholder="Password"
                            showStrength
                          />
                          <InputPassword
                            id="password-confirm"
                            name="password-confirm"
                            placeholder="Confirm password"
                          />
                        </div>
                      )}
                    </Column>
                  </Columns>
                  <Columns count={2} style={{ textAlign: "center" }}>
                    <Column>
                      <div className={layoutClasses.checkbox_label}>
                        <input type="checkbox" name="optinTerms" value="1" /> I
                        accept the R8ER&nbsp;
                        <span className="a" style={{ marginRight: "30px" }}>
                          <a target="_blank" href="/terms">
                            Website Terms of Service
                          </a>
                        </span>
                      </div>
                    </Column>
                    <Column>
                      <div className={layoutClasses.checkbox_label}>
                        <input
                          type="checkbox"
                          name="optinNewsletter"
                          value="1"
                        />{" "}
                        I would like to subscribe to the R8ER Newsletter
                      </div>
                    </Column>
                    {/* <Column>
                      {"entrepreneur" === accountType && (
                        <div className={layoutClasses.checkbox_label}>
                          <input
                            type="checkbox"
                            name="seekingSponsorship"
                            value="0"
                          />{" "}
                          Join sponsorship waitlist
                          <br /> (UK companies only)
                        </div>
                      )}
                    </Column> */}
                  </Columns>

                  <ButtonContainer style={{ marginBottom: "10rem" }}>
                    {errors.length > 0 && (
                      <div className={layoutClasses.text__red}>
                        {errors.map((e) => (
                          <div>{e}</div>
                        ))}
                      </div>
                    )}

                    <ReCAPTCHA
                      recaptchaToken={recaptchaToken}
                      type={needV2Captcha ? "v2" : "v3"}
                    />
                    <button
                      disabled={false}
                      className={[
                        buttonClasses.button_blue,
                        buttonClasses.button_wide,
                      ].join(" ")}
                    >
                      Get Started
                    </button>
                  </ButtonContainer>
                </form>
              )}
            </div>
          </Container>

          <Newsletter />
        </div>
      </Layout>
    </div>
  );
};

const SignUp = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);

  const setAndValidate = (field: string, e) => {
    setEmail(e.target.value);
    setError(!e.target.value.match(/.+@.+\..+/));
  };

  const submitForm = async () => {
    if (error || !email) {
      return;
    }

    setFormSubmitting(true);

    // Send enquiry.
    await api.quickenquiry.create.mutate({
      enquiry: { email: email },
    });

    setFormSubmitted(true);
  };

  return (
    <div>
      <Head>
        <title>Register Interest - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Columns
          count={2}
          style={{
            minHeight: "950px",
            gridColumnGap: 0,
            borderBottom: "8px solid var(--brand-secondary)",
          }}
        >
          <Column
            classList={[columnClasses.marketing__columns_inner_background]}
          >
            <div className={columnClasses.marketing__columns_inner_center_wide}>
              <Watermark />

              <h2 style={{ color: "var(--brand-secondary)" }}>
                Register Interest
              </h2>

              {!formSubmitted && (
                <div>
                  <form>
                    <input
                      className={`${error ? layoutClasses.input_error : ""}`}
                      type="email"
                      placeholder="Email"
                      onChange={(e) => setAndValidate("email", e)}
                    />
                  </form>
                  <button
                    disabled={formSubmitting}
                    className={`button_blue`}
                    onClick={() => submitForm()}
                  >
                    {!formSubmitting ? "Register" : "Sending..."}
                  </button>
                </div>
              )}

              {formSubmitted && (
                <div className={layoutClasses.marketing__notice}>
                  Thank you for registering interest in R8ER.
                </div>
              )}
            </div>
          </Column>
          <Column
            classList={[columnClasses.marketing__columns_inner_overlay]}
            style={{
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundImage: "url(/signin-cover.png)",
            }}
          >
            <div
              className={columnClasses.marketing__columns_inner_center_wide}
              style={{ maxWidth: "550px" }}
            >
              <h2>Coming Soon</h2>
              <p>
                The world's first, professionally vetted, digital evaluation
                platform for disruptive healthcare that connects technology with
                investment.
              </p>
              <div style={{ marginTop: "4rem" }}>
                <IconLogoMarkLight />
              </div>
            </div>
          </Column>
        </Columns>

        <Newsletter />
      </Layout>
    </div>
  );
};

export default function Page({
  showRegistration,
  ...props
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return showRegistration ? <Register {...props} /> : <SignUp />;
}
